import {
  BadgeOutlined,
  CategoryOutlined,
  LocationOnOutlined,
  MeetingRoomOutlined,
  MilitaryTechOutlined,
  PeopleAltOutlined,
  WorkOutlineOutlined,
} from '@mui/icons-material';
import { Alert, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { ResourceState } from '../../../design-system';
import { dateBasisLabel, dateFormatLabel, previewParts } from '../model/employeeNumber';
import type { AsyncResource } from '../../../shared/useAsyncResource';
import type { EmployeeNumberSettings, ReferenceDataItem } from '../model/types';
import { SettingRow } from './SettingRow';
type CollectionResource = AsyncResource<ReferenceDataItem[]>;
export function ReferenceDataOverview({
  canReadStaffing,
  employee,
  locations,
  workSpaces,
  jobFamilies,
  jobProfiles,
  jobLevels,
  jobTypes,
  onOpen,
}: {
  canReadStaffing: boolean;
  employee: AsyncResource<EmployeeNumberSettings>;
  locations: CollectionResource;
  workSpaces: CollectionResource;
  jobFamilies: CollectionResource;
  jobProfiles: CollectionResource;
  jobLevels: CollectionResource;
  jobTypes: CollectionResource;
  onOpen: (view: string) => void;
}) {
  const draft = employee.data;
  const preview = draft ? previewParts(draft) : undefined;
  const count = (resource: CollectionResource) => resource.data?.length ?? 0;
  const value = (resource: CollectionResource) =>
    !canReadStaffing
      ? '조회 권한 필요'
      : resource.loading
        ? <CircularProgress size={16} aria-label="기준정보를 불러오는 중입니다." />
        : resource.error
          ? '불러오지 못함'
          : `${count(resource)}개 등록`;
  return (
    <Stack spacing={2.5}>
      {!canReadStaffing ? (
        <Alert severity="warning">
          근무지·업무 공간·고용 및 외부 인력 유형·직군·직무·직무 레벨을 조회하려면 staffing.read 권한이 필요합니다.
        </Alert>
      ) : null}
      <Stack component="section" aria-labelledby="work-reference-heading" spacing={1}>
        <Typography id="work-reference-heading" component="h2" variant="h5">
          근무 기준
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
          }}
        >
          <Stack divider={<Divider />}>
            <SettingRow
              title="근무지"
              value={value(locations)}
              description="구성원이 근무하는 사업장 기준정보"
              icon={<LocationOnOutlined fontSize="small" />}
              ariaLabel={canReadStaffing ? '근무지 목록 열기' : undefined}
              onClick={canReadStaffing ? () => onOpen('locations') : undefined}
            />
            <SettingRow
              title="업무 공간"
              value={value(workSpaces)}
              description="근무지 안에서 구성원을 배치하는 세부 공간"
              icon={<MeetingRoomOutlined fontSize="small" />}
              ariaLabel={canReadStaffing ? '업무 공간 목록 열기' : undefined}
              onClick={canReadStaffing ? () => onOpen('work-spaces') : undefined}
            />
          </Stack>
        </Paper>
      </Stack>
      <Stack component="section" aria-labelledby="employment-reference-heading" spacing={1}>
        <Typography id="employment-reference-heading" component="h2" variant="h5">
          고용 기준
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
          }}
        >
          <SettingRow
            title="고용·외부 인력 유형"
            value={value(jobTypes)}
            description="임직원의 고용 형태와 외부 인력 유형"
            icon={<PeopleAltOutlined fontSize="small" />}
            ariaLabel={canReadStaffing ? '고용·외부 인력 유형 목록 열기' : undefined}
            onClick={canReadStaffing ? () => onOpen('job-types') : undefined}
          />
        </Paper>
      </Stack>
      <Stack component="section" aria-labelledby="job-reference-heading" spacing={1}>
        <Typography id="job-reference-heading" component="h2" variant="h5">
          직무 기준
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
          }}
        >
          <Stack divider={<Divider />}>
            <SettingRow
              title="직군"
              value={value(jobFamilies)}
              description="직무를 묶는 상위 분류"
              icon={<CategoryOutlined fontSize="small" />}
              ariaLabel={canReadStaffing ? '직군 목록 열기' : undefined}
              onClick={canReadStaffing ? () => onOpen('job-families') : undefined}
            />
            <SettingRow
              title="직무"
              value={value(jobProfiles)}
              description="구성원에게 배정하는 표준 직무"
              icon={<WorkOutlineOutlined fontSize="small" />}
              ariaLabel={canReadStaffing ? '직무 목록 열기' : undefined}
              onClick={canReadStaffing ? () => onOpen('job-profiles') : undefined}
            />
            <SettingRow
              title="직무 레벨"
              value={value(jobLevels)}
              description="승진·보상과 인사 배정에 사용하는 공식 단계"
              icon={<MilitaryTechOutlined fontSize="small" />}
              ariaLabel={canReadStaffing ? '직무 레벨 목록 열기' : undefined}
              onClick={canReadStaffing ? () => onOpen('job-levels') : undefined}
            />
          </Stack>
        </Paper>
      </Stack>
      <Stack component="section" aria-labelledby="policy-heading" spacing={1}>
        <Typography id="policy-heading" component="h2" variant="h5">
          정책
        </Typography>
        <ResourceState
          loading={employee.loading}
          error={employee.error?.message}
          empty={false}
          onRetry={employee.reload}
        >
          <Paper
            variant="outlined"
            sx={{
              overflow: 'hidden',
            }}
          >
            {draft && preview ? (
              <SettingRow
                title="사번 발급 정책"
                value={`${draft.prefix ? `접두어 ${draft.prefix}` : '접두어 없음'} · ${dateBasisLabel(draft.dateBasis)} · ${dateFormatLabel(draft.dateFormat)} · ${draft.sequenceLength}자리`}
                description={`사번 예시 ${preview.prefix}${preview.date}${preview.sequence} · 퇴직자 사번 재사용 ${draft.reuseFormerWorkerId ? '사용' : '사용 안 함'}`}
                icon={<BadgeOutlined fontSize="small" />}
                ariaLabel="사번 발급 정책 설정 열기"
                onClick={() => onOpen('employee-number')}
              />
            ) : null}
          </Paper>
        </ResourceState>
      </Stack>
    </Stack>
  );
}
